// Global (document-level) listener must only be bound once no matter how
// many <mobile-menu> instances exist on the page - otherwise a single click
// on the header's hamburger button would dispatch the toggle event once per
// instance.
let globalMobileMenuListenersBound = false;

function bindGlobalMobileMenuListeners() {
  if (globalMobileMenuListenersBound) return;
  globalMobileMenuListenersBound = true;

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-mobile-menu-toggle]')) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('mobilemenu:toggle'));
    }
  });
}

class MobileMenu extends HTMLElement {
  connectedCallback() {
    bindGlobalMobileMenuListeners();

    this.lastFocused = null;
    const desktopQuery = window.matchMedia('(min-width: 768px)');

    this.syncFromHeader();
    // Theme editor: re-copy when the header re-renders with new settings.
    document.addEventListener('shopify:section:load', () => this.syncFromHeader());

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-mobile-menu-close]')) this.close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });

    document.addEventListener('mobilemenu:toggle', () => {
      if (this.hidden) this.open();
      else this.close();
    });

    // Request from other features (e.g. predictive search) to close the menu.
    // Distinct from `mobilemenu:close`, which is the notification fired *after*
    // the menu closes.
    document.addEventListener('mobilemenu:request-close', () => {
      if (!this.hidden) this.close();
    });

    desktopQuery.addEventListener('change', (event) => {
      if (event.matches) this.close();
    });
  }

  // This section can't read the header's settings, so the header
  // (sections/header.liquid) leaves its logo and mega menu in <template>s.
  // Each is copied independently - either can be missing.
  syncFromHeader() {
    this.syncLogo();
    this.syncMegaMenu();
  }

  // Only present when this section's "Show logo" setting is on.
  syncLogo() {
    const slot = this.querySelector('[data-mobile-menu-logo]');
    if (!slot) return;

    const template = document.querySelector('template[data-mobile-menu-logo]');
    if (!template) {
      slot.replaceChildren();
      return;
    }

    slot.replaceChildren(template.content.cloneNode(true));
  }

  // Copy the mega menu's links into the top of this menu so mobile gets them too.
  syncMegaMenu() {
    const list = this.querySelector('[data-mobile-menu-list]');
    if (!list) return;

    list.querySelector(':scope > [data-mobile-mega-menu-item]')?.remove();

    const template = document.querySelector('template[data-mobile-mega-menu]');
    if (!template) return;

    list.prepend(template.content.cloneNode(true));
  }

  open() {
    this.lastFocused = document.activeElement;
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('header-mobile-menu-open');
    document.dispatchEvent(new CustomEvent('mobilemenu:open'));
    this.querySelector('.mobile-menu__panel')?.focus();
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
    document.documentElement.classList.remove('header-mobile-menu-open');
    document.dispatchEvent(new CustomEvent('mobilemenu:close'));
    if (this.lastFocused) this.lastFocused.focus();
  }
}

// Guarded because this file can legitimately be requested by more than one
// section on the same page - re-registering the same tag name throws.
if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}
